import {toast} from "react-toastify";
import {useDeleteDriverOperationalNoteMutation, useLoadSingleDriverOperationalNoteLazyQuery, useUpsertDriverOperationalNoteMutation} from "../../../__generated__/generated_types";
import {useEffect, useState} from "react";
import {DriverNote} from "../DriverNoteModal";
import {UseDriverNoteModal} from "./useWsDriverNoteModal";


export interface OpDriverNoteModalProps {
    onSave?: () => void;
}

export default function useOpDriverNoteModal(props?: OpDriverNoteModalProps): UseDriverNoteModal {
    const [driverNoteData, setDriverNoteData] = useState<DriverNote | null>(null);

    const [fetchNoteLocal, {data: noteData, loading: noteLoading}] = useLoadSingleDriverOperationalNoteLazyQuery({fetchPolicy: "no-cache"});

    const [deleteNote, {loading: noteDeleting}] = useDeleteDriverOperationalNoteMutation({
        notifyOnNetworkStatusChange: true,
        onCompleted(data) {
            if (data.deleteDriverOperationalNote) {
                toast.success("Note deleted");
                props?.onSave?.();
            }
        },
    });

    const [upsertNote, {loading: noteSaving}] = useUpsertDriverOperationalNoteMutation({
        notifyOnNetworkStatusChange: true,
        onCompleted(data) {
            if (data.upsertDriverOperationalNote) {
                toast.success("Note updated");
                props?.onSave?.();
            }
        },
    });

    useEffect(() => {
        const note = noteData?.loadSingleDriverOperationalNote;
        if (note) {
            setDriverNoteData(prev => {
                if (!prev) {
                    return null;
                }
                return {
                    lineType: "operational",
                    lineId: prev.lineId,
                    lineName: note.lineName,
                    lineValuesLocked: note.lineValuesLocked,
                    note: note.note,
                    isReadOnly: note.isReadOnly,
                };
            });
        }
    }, [noteData]);

    // user experience should be the same whether they are creating or updating a note, so a
    // partial driverNote object is still valid to launch the modal. If the note exists, then as the fetch
    // gets data back it will populate the rest of the object.
    function fetchNote(lineId: string): void {
        setDriverNoteData({
            lineType: "operational",
            lineId,
            lineName: "",
            lineValuesLocked: false,
            note: null,
            isReadOnly: null,
        });

        fetchNoteLocal({
            variables: {
                driverOperationalId: lineId,
            },
        });
    }

    // I figured if the note theyre trying to save is empty, we could just go ahead and delete
    // the note record out of the DB, but if that seems like too heavy of an assumption we can make it an explicit interaction
    function save(driverNote: DriverNote): void {
        const trimmed = driverNote.note?.trim() ?? "";

        if (trimmed === "") {
            deleteNote({
                variables: {
                    driverOperationalId: driverNote.lineId,
                },
            });
        } else {
            upsertNote({
                variables: {
                    note: {
                        lineId: driverNote.lineId,
                        note: driverNote.note ?? "",
                        isReadOnly: driverNote.isReadOnly ?? false,
                    },
                },
            });
        }
    }

    function reset() {
        setDriverNoteData(null);
    }

    return {
        driverNoteData: driverNoteData,
        isBusy: noteLoading || noteSaving || noteDeleting,
        fetchNote: fetchNote,
        save: save,
        reset: reset,
    };
}
